import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink } from 'react-router-dom'
import find from 'lodash/find'

import { AvatarInfo, Paragraph, Text } from 'components/shared'
import UserDropdown, { IUserOption } from '../UserDropdown'
import ArchiveTask from '../ArchiveTask'
import { TaskData, TaskDataList, Wrapper } from '../TaskAside.style'
import { patchTaskAction } from 'store/taskManager/actions'
import { AnyType, Box, COMMON_LINKS, ShowMore, Switch, Tag, useFetch, useHasUserTag, withTags } from '@centra/centra-ui'
import PropertyValuesList from '../PropertyValuesList'
import TaskSurveyButton from '../../../platform/TaskSurvey/_Button'
import { fetchReportCompanyForOldId, getUniqueParticipantList } from '../utils'
import { TaskCompany } from '../../../TaskCompany'
import { CategoryTypeIds } from 'store/taskManager/model'

interface IClientAside {
  isShowExtraMessages: boolean
  onClose: () => void
  onChangeShowExtraMessages: () => void
}

/**
 * ВАЖНО!!!!
 * ВАЖНО!!!!
 * ВАЖНО!!!! Можно было подумать, что это только для клиентов так как это ClientSide, а есть еще и AccountAside
 * ВАЖНО!!!! Но этот компонент работает также и для Админа
 * ВАЖНО!!!!
 * ВАЖНО!!!!
 */
const ClientAside = (props: IClientAside) => {
  const { isShowExtraMessages, onClose, onChangeShowExtraMessages } = props
  const dispatch = useDispatch()
  const { task, user } = useSelector(({ taskManager, auth }) => ({
    task: taskManager.task,
    user: auth.user,
  }))
  const [assignedToOptions, setAssignedToOptions] = useState<IUserOption[]>([])

  const participants = useMemo(() => {
    if (task?.lastCommunicationUser) {
      return getUniqueParticipantList([...(task.participants || []), ...[task.lastCommunicationUser]])
    }
    return task?.participants || []
  }, [task])

  useEffect(() => {
    if (task) {
      // после изменения таска участники тикета и доступные исполнители могут быть изменены
      // Пока только для "Поддержка по продуктам"
      setAssignedToOptions(participants)
    }
  }, [task])

  const isClient = useHasUserTag([
    'role.agency-primary-manager',
    'role.agency-manager',
    'role.client-primary-manager',
    'role.client-manager',
  ])
  const isEmployeeUser = useHasUserTag(['employeeUser'])

  const canChangeAssignee = useMemo(() => task?.category?.id !== CategoryTypeIds.ai, [task])

  const canBeArchived = useMemo(() => {
    return (
      withTags({ user: user as AnyType, tags: ['role.supervisor', 'role.admin'] }) &&
      (task?.status.id === 4 || task?.status.id === 5)
    )
  }, [user, task])

  const doFetchReportCompanyForOldId = useFetch({
    cb: fetchReportCompanyForOldId,
    errorMessage: {
      title: 'Ошибка',
      content: 'Произошла ошибка при загрузке информации о клиенте',
    },
  })
  useEffect(() => {
    if (task?.client?.id && !isClient) {
      doFetchReportCompanyForOldId.fetch(task.client.id)
    }
  }, [task])

  const clientLink = useMemo(() => {
    if (task && !isClient) {
      // Сюда может попасть например Админ и РАМ, но точно не АМ. Для них иди в AccountAside
      const {
        client: { id },
      } = task

      return `${COMMON_LINKS.CUSTOMER.href}/client/${id}`
    }
    return null
  }, [task?.client, isClient, doFetchReportCompanyForOldId.result])

  const participantItems = useMemo(() => {
    return task?.participants?.map(participant => (
      <Box key={participant.id} marginBottom={12}>
        <AvatarInfo
          size={'md'}
          avatar={participant.avatar}
          firstName={participant.firstName}
          lastName={participant.lastName}
          storage={false}
          grayscale
        >
          {/** marginBottom нужен для компенсации отсупа аватара, чтобы не менять верстку полностью */}
          <Paragraph size={'sm'} weight={'bold'} marginBottom={participant.title ? undefined : 12}>
            {participant.firstName} {participant.lastName}
          </Paragraph>
          {participant.title && (
            <Paragraph className={'grey'} size={'sm'}>
              {participant.title}
            </Paragraph>
          )}
        </AvatarInfo>
      </Box>
    ))
  }, [task?.participants])

  const onChangeAssigned = (id: number) => {
    if (task && task.assigned?.id !== id) {
      const assigned = find(task?.participants, { id })
      dispatch(
        patchTaskAction({
          id: task?.id,
          assigned: assigned,
        })
      )
    }
  }

  return (
    <Wrapper>
      {task && (
        <>
          <TaskDataList>
            <TaskData>
              <label>категория</label>
              <Box style={{ marginLeft: '-8px' }}>
                <Tag
                  id={`task_${task.id}_category_tag`}
                  size={'sm'}
                  variant={'outline'}
                  view={task.category.colorAlias}
                >
                  {task.category.name}
                </Tag>
              </Box>
            </TaskData>
            <TaskData>
              <label>Статус</label>
              <Box style={{ marginLeft: '-8px' }}>
                <Tag id={`task_${task.id}_status_tag`} size={'sm'} view={task.status.colorAlias}>
                  {task.status.name}
                </Tag>
              </Box>
            </TaskData>
            <TaskData>
              <label>Клиент</label>
              {clientLink ? (
                <NavLink to={clientLink} exact>
                  <TaskCompany
                    client={task.client}
                    fontSize={'caption'}
                    fontWight={'semiBold'}
                    fontColor={'basicBlack'}
                  />
                </NavLink>
              ) : (
                <TaskCompany
                  client={task.client}
                  fontSize={'caption'}
                  fontWight={'semiBold'}
                  fontColor={'basicBlack'}
                />
              )}
            </TaskData>
            <TaskData>
              <label>участники</label>
              {participantItems && (
                <Box column>
                  <ShowMore htmlContent={participantItems} maxItems={3} btnSize={'sm'} isToggle />
                </Box>
              )}
            </TaskData>
            <TaskData>
              <label>Исполнитель</label>
              {isClient && canChangeAssignee ? (
                <>
                  <UserDropdown list={assignedToOptions} value={task.assigned || null} onChange={onChangeAssigned} />
                </>
              ) : (
                <AvatarInfo
                  size={'md'}
                  avatar={task.assigned.avatar}
                  firstName={task.assigned.firstName}
                  lastName={task.assigned.lastName}
                  storage={false}
                  grayscale
                >
                  <Paragraph size={'sm'} weight={'bold'}>
                    {task.assigned.firstName} {task.assigned.lastName}
                  </Paragraph>
                  <Paragraph className={'grey'} size={'sm'}>
                    {task.assigned.title}
                  </Paragraph>
                </AvatarInfo>
              )}
            </TaskData>
            <TaskData>
              <label>Номер задачи</label>
              <Paragraph size={'sm'}>
                <b>{task.id}</b>
              </Paragraph>
            </TaskData>

            <PropertyValuesList />

            {isClient && <TaskSurveyButton taskId={task.id} />}
          </TaskDataList>
          {canBeArchived && <ArchiveTask taskId={task.id} onClose={onClose} />}

          {isEmployeeUser && (
            <Box>
              <Switch id={'showExtraMessagesSwitch'} value={isShowExtraMessages} onChange={onChangeShowExtraMessages} />
              <Box marginLeft={8}>
                <Text size={'sm'}>Показать больше истории тикета</Text>
              </Box>
            </Box>
          )}
        </>
      )}
    </Wrapper>
  )
}

export default ClientAside
